import { type MutationUpdaterFn } from "@apollo/client";

import { type DataProxy } from "../../../core/types/apollo";
import { type CreateGroupV2Mutation } from "../../../dashboard/api/mutations/createGroupV2.gql-generated";
import { type LensDetailsConnectionFragment } from "../../../lenses/api/fragments";
import { type GroupConnectionsFragment, groupConnectionsFragment } from "../fragments";
import { type GetCachedGroupListV2Query, getCachedGroupListV2 } from "../queries";

import { GroupOperations } from "./GroupOperations";
import { GroupsQueryServiceV2 } from "./GroupsQueryService";

interface Cache {
    addGroup: MutationUpdaterFn<CreateGroupV2Mutation>;
    removeGroup: (cache: DataProxy, groupId: string) => void;
    addConnectionIfNotExists: (cache: DataProxy, connection: LensDetailsConnectionFragment) => void;
    removeConnection: (cache: DataProxy, groupId: string, connectionId: string) => void;
    removeAllConnections: (cache: DataProxy, groupId: string) => void;
    createGroupWithConnectionsId(groupId: string): string;
}

export const GroupCache: Cache = {
    createGroupWithConnectionsId(groupId: string) {
        const key = {
            group: {
                id: groupId,
            },
        };
        return `GroupWithConnections:${JSON.stringify(key)}`;
    },

    addGroup(cache, { data }) {
        const saveGroupV2 = data?.saveGroupV2;
        const groupList = cache.readQuery<GetCachedGroupListV2Query>({
            query: getCachedGroupListV2,
        }) as GetCachedGroupListV2Query;

        cache.writeQuery({
            query: getCachedGroupListV2,
            data: GroupsQueryServiceV2.addGroup(groupList, saveGroupV2),
        });
    },

    removeGroup(cache, groupId) {
        const groupList = cache.readQuery<GetCachedGroupListV2Query>({
            query: getCachedGroupListV2,
        }) as GetCachedGroupListV2Query;
        cache.writeQuery({
            query: getCachedGroupListV2,
            data: GroupsQueryServiceV2.removeGroup(groupList, groupId),
        });
    },

    addConnectionIfNotExists(cache, createConnection) {
        const { group: groupConnection } = createConnection;
        const groupId = groupConnection?.id ?? "";
        const cacheId = GroupCache.createGroupWithConnectionsId(groupId);

        const group = cache.readFragment<GroupConnectionsFragment>({
            id: cacheId,
            fragment: groupConnectionsFragment,
        });

        if (group) {
            cache.writeFragment({
                id: cacheId,
                fragment: groupConnectionsFragment,
                data: GroupOperations.addConnectionIfNotExists(group, createConnection),
            });
        }
    },

    removeConnection(cache, groupId, connectionId) {
        const group = cache.readFragment<GroupConnectionsFragment>({
            id: GroupCache.createGroupWithConnectionsId(groupId),
            fragment: groupConnectionsFragment,
        });

        if (group) {
            cache.writeFragment<GroupConnectionsFragment>({
                id: GroupCache.createGroupWithConnectionsId(groupId),
                fragment: groupConnectionsFragment,
                data: GroupOperations.removeConnection(group, connectionId),
            });
        }
    },

    removeAllConnections(cache, groupId) {
        const group = cache.readFragment<GroupConnectionsFragment>({
            id: GroupCache.createGroupWithConnectionsId(groupId),
            fragment: groupConnectionsFragment,
        });

        if (group) {
            cache.writeFragment<GroupConnectionsFragment>({
                id: GroupCache.createGroupWithConnectionsId(groupId),
                fragment: groupConnectionsFragment,
                data: GroupOperations.removeAllConnections(group),
            });
        }
    },
};
